import React from 'react';
import { useEnvironment } from '@/hooks/useEnvironment';
import { ArrowLeft, Mail, Calendar, Shield, FileText, Users, Lock, AlertTriangle, CheckCircle } from 'lucide-react';

const DataProcessingandAgreement: React.FC = () => {
  const { isNativeWebView } = useEnvironment();

  // Desktop/Web Design - Full featured layout
  const WebDesign = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            DATA PROCESSING AGREEMENT (DPA)
          </h1>
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            <Calendar className="w-4 h-4 mr-2" />
            Effective Date: ____________________
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 sm:p-8 lg:p-12 space-y-8">
              
              {/* Company Header */}
              <section className="border-l-4 border-blue-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Duri App Technologies Pvt. Ltd.</h2>
                    <p className="text-gray-700 leading-relaxed">
                      This Data Processing Agreement ("Agreement") is entered into between Duri App Technologies Pvt. Ltd. ("Duri App") and the Supplier ("Supplier") identified in the Main Agreement.
                    </p>
                  </div>
                </div>
              </section>

              {/* Introduction */}
              <section className="border-l-4 border-green-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Introduction to Duri App's Data Processing Agreement</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Duri App requires that its suppliers agree to terms set forth in its Data Processing Agreement (DPA) governing the processing of Duri App Confidential Information, including Duri App Personal Data.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        These terms are intended to protect the confidentiality and security of Duri App's Confidential Information, and enable Duri App to meet its requirements under global data protection laws, including as applicable (based on suppliers' processing of Duri App Personal Data) the European Union's General Data Protection Regulation (GDPR), Brazil's Lei Geral de Proteção de Dados Pessoais (LGPD), Australia's Privacy Act, India's Digital Personal Data Protection Act and U.S. state privacy laws, including the California Consumer Privacy Act (CCPA).
                    </p>
                  </div>
                </div>
              </section>

              {/* DPA Addresses */}
              <section className="border-l-4 border-purple-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">The DPA specifically addresses:</h2>
                    <div className="grid gap-3 mt-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">General requirements and limitations regarding suppliers' processing of Duri App Confidential Information, including as regarding data security, data security incidents, risk assessments and audits, data retention and deletion, and use of sub-processors.</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Designation of the roles played by Duri App and suppliers as regards the processing of Duri App Personal Data. The DPA specifically requires that the parties designate whether each is acting as: a Controller or Processor, Business, Service Provider, Contractor or Third Party as those terms are defined under the CCPA.</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">The DPA also specifies the responsibilities of each party based on the above designations.</span>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      Please notify your Duri App contact if you have questions regarding Duri App's DPA.
                    </p>
                  </div>
                </div>
              </section>

              {/* CHALOAPP DPA */}
              <section className="border-l-4 border-indigo-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">DURI APP DATA PROCESSING AGREEMENT</h2>
                    <p className="text-gray-700 leading-relaxed">
                      This Data Processing Agreement ("Agreement") sets forth the requirements applicable to Supplier's Processing of: (1) Duri App Confidential Information, including Duri App Personal Data (see Section 2).
                    </p>
                  </div>
                </div>
              </section>

              {/* Definitions */}
              <section className="border-l-4 border-yellow-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Definitions</h2>
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Data Security Incident:</h4>
                        <p className="text-gray-700 text-sm">any unauthorized access to, unauthorized use of, unauthorized disclosure of, or loss of Duri App Confidential Information, or compromise of the security or integrity of such Duri App Confidential Information or any System used by Supplier, or its Sub-processors, to Process such data.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Delete:</h4>
                        <p className="text-gray-700 text-sm">to physically or logically destroy Duri App Confidential Information so that it cannot be recovered.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Discovery:</h4>
                        <p className="text-gray-700 text-sm">any instance in which Supplier discovers a Data Security Incident, is notified of a Data Security Incident, or would have discovered a Data Security Incident had it exercised reasonable diligence.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Personal Data:</h4>
                        <p className="text-gray-700 text-sm">any information that relates to an identified or identifiable Data Subject.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Process:</h4>
                        <p className="text-gray-700 text-sm">any operation or set of operations which is performed on Personal Data or on sets of Personal Data, whether or not by automated means, such as collection, recording, organisation, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission, dissemination or otherwise making available, alignment or combination, restriction, erasure or destruction.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Processor:</h4>
                        <p className="text-gray-700 text-sm">a natural or legal entity that processes personal data on behalf of a Controller.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Sub-processor:</h4>
                        <p className="text-gray-700 text-sm">a Processor engaged by Supplier to Process Duri App Confidential Information.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">System:</h4>
                        <p className="text-gray-700 text-sm">any file system, computing system, database, device, equipment, server, website, application, software, storage media, network, infrastructure, networked environment or domain, including, without limitation, all development, quality assurance, staging and production environments.</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Duri App:</h4>
                        <p className="text-gray-700 text-sm">Duri App Technologies Pvt. Ltd. and any subsidiary or affiliate of Duri App.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Security Measures */}
              <section className="border-l-4 border-red-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Security Measures</h2>
                    <p className="text-gray-700 leading-relaxed">
                      For purposes of this Agreement, Duri App Personal Data does not include the name and contact information and confidentiality of Duri App Confidential Information, which measures shall include at a minimum those set forth in Appendix 1 to this Agreement.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg mt-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                        <Lock className="w-4 h-4 mr-2" />
                        Security Manager
                      </h4>
                      <p className="text-blue-800 text-sm">
                        Supplier shall designate an individual responsible for managing and coordinating the performance of Supplier's obligations under this Agreement, and making them available to Duri App throughout the term of the Main Agreement.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Security Incidents */}
              <section className="border-l-4 border-pink-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">7</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Security Incidents Investigation</h2>
                    <div className="space-y-4">
                      <p className="text-gray-700 leading-relaxed">
                        Supplier shall take all reasonable steps upon Discovery of a Data Security Incident to fully and thoroughly investigate the cause, nature, and scope of compromise of such Data Security Incident at Supplier's expense; remediate and mitigate the effects of the Data Security Incident; and provide to Duri App all information required or requested for Duri App to comply with applicable laws and internal Data Security Incident response processes.
                      </p>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Notice to Duri App
                        </h4>
                        <p className="text-red-800 text-sm mb-2">
                          Supplier shall notify Duri App within forty-eight (48) hours of Discovery via the Duri App persons or team designated to receive notices under the Main Agreement, and via email to security@duriapp.com.
                        </p>
                        <p className="text-red-800 text-sm">
                          Supplier shall include in such notice, and supplement thereafter as necessary: a description of the Data Security Incident, including the cause (if identifiable), location, date/time and the Data Security Incident and its Discovery; a description of the steps Supplier has taken or will take to investigate, and mitigate the impact of, the Data Security Incident; the types and volume of affected Duri App Confidential Information.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Retention and Deletion */}
              <section className="border-l-4 border-teal-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">8</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Retention and Deletion</h2>
                    <p className="text-gray-700 leading-relaxed">
                      Supplier shall promptly Delete or return to Duri App (at Duri App's election) all Duri App Confidential Information in its possession, custody and control: (i) upon termination or expiration of the Main Agreement; (ii) upon the winding down or insolvency of the Supplier's business; (iii) once no longer necessary to perform its obligations under the Main Agreement; or (iv) upon request by Duri App.
                    </p>
                  </div>
                </div>
              </section>

              {/* Risk Assessments */}
              <section className="border-l-4 border-orange-500 pl-6">
                <div className="flex items-start space-x-4">    
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">9</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Risk Assessments</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Supplier shall complete and pass an information security risk assessment (Risk Assessment) as reasonably requested by Duri App, including but not limited to when: (i) Duri App has reasonable concerns regarding Supplier's information security practices; (ii) the Main Agreement is being renewed or extended; (iii) Supplier's information security practices or the scope of services change materially; (iv) Duri App determines that an assessment is necessary for Duri App to comply with Data Protection Laws or other data security compliance obligations; (vi) an assessment is reasonably necessary for Duri App to comply with a request, order, or settlement with a supervisory or other legal obligation; or (vii) a Data Security Incident occurs.
                    </p>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-orange-900 mb-2">Requirements</h4>
                      <p className="text-orange-800 text-sm">
                        Supplier shall provide to Duri App all information reasonably necessary to complete the Risk Assessment. Such information may include, but is not limited to, risk assessment questionnaires; information security policies and procedures; data classification and handling policies and procedures; data security compliance or audit reports that assess the effectiveness of Supplier's information security program, system(s), internal controls, and procedures relating to the Processing of Duri App Personal Data against an industry-accepted framework such as ISO, SSAE16, SOC, or NIST.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Subprocessors */}
              <section className="border-l-4 border-emerald-500 pl-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold">10</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Subprocessors</h2>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Permitted Sub-Processors
                      </h4>
                      <p className="text-red-800 text-sm">
                        Supplier shall NOT permit any Sub-processor to Process Duri App Confidential Information.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 sm:px-8 lg:px-12 py-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <p className="text-sm text-gray-600 text-center sm:text-left">
                  © 2025 Duri App Technologies Pvt. Ltd. All rights reserved.
                </p>
                {/* <div className="flex items-center space-x-4">
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                    Privacy Policy
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                    Terms of Service
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="fixed bottom-8 right-8">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center group"
          >
            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // Mobile WebView Design - iPhone-style, clean, edge-to-edge
  const WebViewDesign = () => (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      

      {/* Content */}
      <div className="space-y-3 px-4 py-4 pb-20">
        
        {/* Header Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-sm p-6 border border-blue-100">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-2xl mb-4 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-bold text-gray-900 text-xl mb-3">DATA PROCESSING AGREEMENT</h1>
            <div className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-medium shadow-sm">
              <Calendar className="w-4 h-4 mr-2" />
              Last Updated: October 10, 2025
            </div>
          </div>
        </div>

        {/* Company Header */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">1</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Duri App Technologies Pvt. Ltd.</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                This Data Processing Agreement ("Agreement") is entered into between Duri App Technologies Pvt. Ltd. ("Duri App") and the Supplier ("Supplier") identified in the Main Agreement.
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-green-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">2</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Introduction to Duri App's Data Processing Agreement</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                Duri App requires that its suppliers agree to terms set forth in its Data Processing Agreement (DPA) governing the processing of Duri App Confidential Information, including Duri App Personal Data.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                  These terms are intended to protect the confidentiality and security of Duri App's Confidential Information, and enable Duri App to meet its requirements under global data protection laws, including GDPR, LGPD, Australia's Privacy Act, India's Digital Personal Data Protection Act and U.S. state privacy laws, including the CCPA.
              </p>
            </div>
          </div>
        </div>

        {/* DPA Addresses */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-purple-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">3</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">The DPA specifically addresses:</h3>
              <div className="space-y-3 mt-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 text-purple-600" />
                  </div>
                  <span className="text-gray-700 text-sm">General requirements and limitations regarding suppliers' processing of Duri App Confidential Information.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 text-purple-600" />
                  </div>
                  <span className="text-gray-700 text-sm">Designation of roles played by Duri App and suppliers as regards processing of Duri App Personal Data.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 text-purple-600" />
                  </div>
                  <span className="text-gray-700 text-sm">The DPA specifies responsibilities of each party based on designations.</span>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg mt-4">
                <p className="text-blue-800 text-sm">
                  Please notify your Duri App contact if you have questions regarding Duri App's DPA.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CHALOAPP DPA */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-indigo-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">4</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">DURI APP DATA PROCESSING AGREEMENT</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                This Data Processing Agreement ("Agreement") sets forth the requirements applicable to Supplier's Processing of: (1) Duri App Confidential Information, including Duri App Personal Data.
              </p>
            </div>
          </div>
        </div>

        {/* Definitions */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-yellow-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">5</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Key Definitions</h3>
              <div className="space-y-3 mt-3">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-yellow-600" />
                    Data Security Incident:
                  </h4>
                  <p className="text-gray-700 text-xs leading-relaxed">any unauthorized access to, unauthorized use of, unauthorized disclosure of, or loss of Duri App Confidential Information.</p>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-yellow-600" />
                    Personal Data:
                  </h4>
                  <p className="text-gray-700 text-xs leading-relaxed">any information that relates to an identified or identifiable Data Subject.</p>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-yellow-600" />
                    Processor:
                  </h4>
                  <p className="text-gray-700 text-xs leading-relaxed">a natural or legal entity that processes personal data on behalf of a Controller.</p>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-yellow-600" />
                    Duri App:
                  </h4>
                  <p className="text-gray-700 text-xs leading-relaxed">Duri App Technologies Pvt. Ltd. and any subsidiary or affiliate of Duri App.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Measures */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-red-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">6</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Security Measures</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                For purposes of this Agreement, Duri App Personal Data does not include the name and contact information and confidentiality of Duri App Confidential Information.
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                <h4 className="font-bold text-blue-900 text-sm mb-2 flex items-center">
                  <Lock className="w-4 h-4 mr-2" />
                  Security Manager
                </h4>
                <p className="text-blue-800 text-xs leading-relaxed">
                  Supplier shall designate an individual responsible for managing and coordinating the performance of Supplier's obligations under this Agreement.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Security Incidents */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-pink-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">7</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Data Security Incidents Investigation</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Supplier shall take all reasonable steps upon Discovery of a Data Security Incident to fully and thoroughly investigate the cause, nature, and scope of compromise.
              </p>
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                <h4 className="font-bold text-red-900 text-sm mb-2 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Notice to Duri App
                </h4>
                <p className="text-red-800 text-xs leading-relaxed">
                  Supplier shall notify Duri App within forty-eight (48) hours of Discovery via email to security@duriapp.com.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Retention and Deletion */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-teal-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">8</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Data Retention and Deletion</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Supplier shall promptly Delete or return to Duri App all Duri App Confidential Information in its possession upon termination, expiration, winding down, or upon request by Duri App.
              </p>
            </div>
          </div>
        </div>

        {/* Risk Assessments */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-orange-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">9</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Risk Assessments</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Supplier shall complete and pass an information security risk assessment as reasonably requested by Duri App, including when concerns arise or when required for compliance.
              </p>
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                <h4 className="font-bold text-orange-900 text-sm mb-2">Requirements</h4>
                <p className="text-orange-800 text-xs leading-relaxed">
                  Supplier shall provide all information reasonably necessary to complete the Risk Assessment, including security policies, procedures, and compliance reports.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Subprocessors */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 active:scale-[0.98] transition-transform duration-150">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-emerald-500 text-white rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm">10</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-base mb-3">Subprocessors</h3>
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                <h4 className="font-bold text-red-900 text-sm mb-2 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Permitted Sub-Processors
                </h4>
                <p className="text-red-800 text-xs leading-relaxed">
                  Supplier shall NOT permit any Sub-processor to Process Duri App Confidential Information.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl shadow-sm p-6 mt-4 border border-gray-200">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-xl mb-3">
              <Shield className="w-6 h-6 text-gray-600" />
            </div>
            <p className="text-sm text-gray-700 mb-4 font-medium">
              © 2025 Duri App Technologies Pvt. Ltd.
            </p>
            <p className="text-xs text-gray-500">
              All rights reserved. This document is protected by confidentiality agreements.
            </p>
          </div>
        </div>

      </div>
    </div>
  );

  // Conditional rendering based on environment
  return isNativeWebView ? <WebViewDesign /> : <WebDesign />;
};

export default DataProcessingandAgreement;
